/* eslint-disable jsx-a11y/no-onchange */
import { FC, useEffect, useState } from 'react'
import classNamesBind from 'classnames/bind'
import { useTranslation } from 'react-i18next'
import { AddressFields, AddressFormProps as Props } from './types'
import { getTown } from 'api/customerFlow'
import { isValidPostalCode } from 'utils/helpers'
import { Towns } from 'types/towns'
import { LettersNumbersSlashesAndDashes } from 'constants/regex'
import { preventArrowUpAndDownEvent, preventInputScrollEvent } from 'utils/eventHandlers'

const AddressForm: FC<Props> = ({ input, isNested, type, className, register, errors, setValue }) => {
  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation', 'simulation'])

  // Local state
  const [towns, setTowns] = useState<Towns>([])

  /**
   * Triggered on first render
   * Checks if town names should be fetched based on postalCode in store
   */
  useEffect(() => {
    const postalCode = input?.postalCode

    if (postalCode !== '') validatePostalCodeAndfetchTowns(parseInt(postalCode, 10))
  }, [])

  /**
   * Fetches the town names based on the given postalCode
   *
   * @param postalCode
   */
  const validatePostalCodeAndfetchTowns = async (postalCode: number) => {
    if (isValidPostalCode(postalCode)) {
      const response = await getTown(postalCode)

      if (response !== null) {
        const { towns } = response

        setTowns(towns)

        if (!towns.some((town) => town.townName === input?.townName)) {
          setValue(getFieldName(AddressFields.TOWN_NAME), towns[0].townName)
          setValue(getFieldName(AddressFields.TOWN_CODE), towns[0].townCode)
        }

        return true
      }
    }

    return false
  }

  /**
   * Returns the css classes for the given field
   *
   * @param field
   * @returns string
   */
  const getFieldClassNames = (field: AddressFields) => {
    return classNamesBind('is-block', {
      'has-error': !isNested ? type in errors && field in errors[type] : !!errors?.[field]
    })
  }

  /**
   * Returns the errors for the given field in the current form type
   *
   * @param field
   * @returns
   */
  const getFieldErrors = (field: AddressFields, attribute: string) => {
    return !isNested
      ? errors?.[type]?.[field] && (
          <p className="margin-top-s margin-bottom-none is-red">
            {errors[type][field].type === 'required' && t('validation:required')}
            {errors[type][field].type === 'maxLength' &&
              t('validation:max.string', {
                attribute,
                length: 5
              })}

            {/* CUSTOM VALIDATION */}
            {field === AddressFields.NUMBER && errors[type][field].type === 'pattern' && t('validation:address.number.pattern')}
            {field === AddressFields.POSTAL_CODE && errors[type][field].type === 'validate' && t('simulation:invalidPostalCode')}
          </p>
        )
      : errors?.[field] && (
          <p className="margin-top-s margin-bottom-none is-red">
            {errors?.[field].type === 'required' && t('validation:required')}
            {errors?.[field].type === 'maxLength' &&
              t('validation:max.string', {
                attribute,
                length: 5
              })}

            {/* CUSTOM VALIDATION */}
            {field === AddressFields.NUMBER && errors?.[field].type === 'pattern' && t('validation:address.number.pattern')}
            {field === AddressFields.POSTAL_CODE && errors?.[field].type === 'validate' && t('simulation:invalidPostalCode')}
          </p>
        )
  }

  /**
   * Returns the field name based on the given field & current form type
   *
   * @param field
   * @returns string
   */
  const getFieldName = (field: AddressFields) => {
    return `${type}.${field}`
  }

  /**
   * Handles the town select change & sets the town code
   *
   * @param selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const townCode = towns.find((town) => town.townName === selectedTown)?.townCode ?? 0

    setValue(getFieldName(AddressFields.TOWN_CODE), townCode)
  }

  return (
    <div className={className}>
      <div className="form-row is-2">
        {/* STREET */}
        <div className="form-input">
          <label htmlFor={getFieldName(AddressFields.STREET)} className="margin-bottom-xs">
            {t('street', 'Straat')}
          </label>
          <input
            type="text"
            className={getFieldClassNames(AddressFields.STREET)}
            {...register(getFieldName(AddressFields.STREET), { required: true })}
          />
          {getFieldErrors(AddressFields.STREET, t('street', 'Straat'))}
        </div>

        {/* NUMBER */}
        <div className="form-row is-2">
          <div className="form-input">
            <label htmlFor={getFieldName(AddressFields.NUMBER)} className="margin-bottom-xs">
              {t('number', 'Nummer')}
            </label>
            <input
              type="text"
              maxLength={5}
              className={getFieldClassNames(AddressFields.NUMBER)}
              {...register(getFieldName(AddressFields.NUMBER), { required: true, maxLength: 5, pattern: LettersNumbersSlashesAndDashes })}
            />
            {getFieldErrors(AddressFields.NUMBER, t('number', 'Nummer'))}
          </div>

          {/* BOX */}
          <div className="form-input">
            <label htmlFor={getFieldName(AddressFields.BOX)} className="margin-bottom-xs">
              {t('box', 'Bus')}
            </label>
            <input
              type="text"
              maxLength={5}
              className={getFieldClassNames(AddressFields.BOX)}
              {...register(getFieldName(AddressFields.BOX), { maxLength: 5 })}
            />
            {getFieldErrors(AddressFields.BOX, t('box', 'Bus'))}
          </div>
        </div>
      </div>

      <div className="form-row is-2">
        {/* POSTAL CODE */}
        <div className="form-input">
          <label htmlFor={getFieldName(AddressFields.POSTAL_CODE)}>{t('zipcode', 'Postcode')}</label>
          <input
            type="number"
            pattern="[0-9]*"
            inputMode="numeric"
            className={getFieldClassNames(AddressFields.POSTAL_CODE)}
            {...register(getFieldName(AddressFields.POSTAL_CODE), {
              required: true,
              validate: (val) => validatePostalCodeAndfetchTowns(val)
            })}
            onChange={(e) => validatePostalCodeAndfetchTowns(parseInt(e.target.value, 10))}
            onWheel={(e) => preventInputScrollEvent(e)}
            onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
          />
          {getFieldErrors(AddressFields.POSTAL_CODE, t('zipcode', 'Postcode'))}
        </div>

        {/* CITY */}
        <div className="form-input">
          <label htmlFor={getFieldName(AddressFields.TOWN_NAME)}>{t('city', 'Gemeente')}</label>
          <select
            className={getFieldClassNames(AddressFields.TOWN_NAME)}
            {...register(getFieldName(AddressFields.TOWN_NAME), { required: true })}
            onChange={(e) => handleTownSelectChange(e.currentTarget.value)}
          >
            <option disabled>{t('select', 'selecteer...')}</option>
            {towns.map((town, idx) => (
              <option key={`town.geoCode-${idx}`} value={town.townName} selected={input?.townName === town.townName}>
                {town.townName}
              </option>
            ))}
          </select>
          {getFieldErrors(AddressFields.TOWN_NAME, t('city', 'Gemeente'))}

          <input type="hidden" {...register(getFieldName(AddressFields.TOWN_CODE))} />
        </div>
      </div>
    </div>
  )
}

export default AddressForm
